import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Inbox, RefreshCw } from 'lucide-react';
import { marketingAdmin } from '../../services/marketingAdminService';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
];

function parseEnterpriseMessage(message) {
  const raw = String(message || '').trim();
  const match = raw.match(/^\[Enterprise\]\s*Company:\s*(.*?)\s*\|\s*([\s\S]*)$/);
  if (!match) return { company: '', note: raw };
  const company = match[1] === '-' ? '' : match[1].trim();
  return { company, note: (match[2] || '').trim() };
}

function fmtWhen(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusChip(status) {
  const s = String(status || 'new').toLowerCase();
  if (s === 'converted') return 'bg-emerald-50 text-emerald-700';
  if (s === 'contacted') return 'bg-blue-50 text-blue-700';
  if (s === 'closed') return 'bg-slate-100 text-slate-600';
  return 'bg-amber-50 text-amber-800';
}

export default function EnterpriseInquiries() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: rows = [], isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['enterpriseWebsiteInquiries'],
    queryFn: async () => {
      const { data, error: qErr } = await marketingAdmin
        .from('website_leads')
        .select('*')
        .eq('source', 'enterprise_page')
        .order('created_at', { ascending: false })
        .limit(500);
      if (qErr) throw qErr;
      return Array.isArray(data) ? data : [];
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const { error: uErr } = await marketingAdmin
        .from('website_leads')
        .update({ status })
        .eq('id', id);
      if (uErr) throw uErr;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['enterpriseWebsiteInquiries'] }),
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const { company, note } = parseEnterpriseMessage(row.message);
      if (statusFilter !== 'all' && String(row.status || 'new') !== statusFilter) return false;
      if (!term) return true;
      const hay = [row.name, row.phone, company, note, row.page_url].join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [rows, search, statusFilter]);

  const newCount = rows.filter((r) => String(r.status || 'new') === 'new').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/enterprises')}
            className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:underline"
          >
            <ArrowLeft size={16} /> Enterprises
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Enterprise inquiries</h1>
          <p className="mt-1 text-gray-600">
            Forms submitted on the marketing site /enterprise page
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-600">Total inquiries</p>
          <p className="text-2xl font-bold text-gray-900">{rows.length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-600">New</p>
          <p className="text-2xl font-bold text-amber-600">{newCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-600">Showing</p>
          <p className="text-2xl font-bold text-gray-900">{filtered.length}</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, phone, company, notes"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:max-w-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Could not load inquiries. {error.message || 'Check website_leads access.'}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <p className="p-8 text-center text-gray-500">Loading inquiries...</p>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            <Inbox className="mx-auto mb-3 text-gray-300" size={40} />
            No enterprise inquiries yet. New forms from localhost:3010/enterprise will show here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((row) => {
                  const { company, note } = parseEnterpriseMessage(row.message);
                  return (
                    <tr key={row.id} className="align-top">
                      <td className="whitespace-nowrap px-4 py-3 text-gray-500">{fmtWhen(row.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{row.name || '—'}</td>
                      <td className="px-4 py-3">
                        {row.phone ? (
                          <a href={`tel:${row.phone}`} className="font-semibold text-primary-700 hover:underline">
                            {row.phone}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-800">{company || '—'}</td>
                      <td className="max-w-xs px-4 py-3 text-gray-600">{note || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          <span className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-semibold ${statusChip(row.status)}`}>
                            {row.status || 'new'}
                          </span>
                          <select
                            value={row.status || 'new'}
                            disabled={statusMutation.isPending}
                            onChange={(e) =>
                              statusMutation.mutate({ id: row.id, status: e.target.value })
                            }
                            className="rounded border border-gray-300 px-2 py-1 text-xs"
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
